/**
 * Canonical-shape helpers with no platform dependency.
 *
 * These are split out of `./content` so that browser-reachable code can
 * canonicalise and validate content refs WITHOUT pulling in the synchronous
 * SHA-256 in `./content`, which needs `node:crypto`. `./content` re-exports
 * everything here, so every existing importer of `./content` (and of the
 * package root) keeps its full surface — this file adds a door, it does not
 * move one.
 *
 * Nothing in this file may import a `node:` builtin. `@cogo/acceptance-contracts`
 * exposes its browser-safe barrel on top of this module, and
 * `apps/cogo-studio/src/__tests__/browser-graph-node-builtins.test.ts` fails if
 * that stops being true.
 */
import type { ContentHash } from "./types";

const HASH_PATTERN = /^sha256:[a-f0-9]{64}$/;
const IDENTIFIER_PATTERN = /^[A-Za-z][A-Za-z0-9_.:/#-]*$/;

export function compareCanonical(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function canonicalize(value: unknown): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("NON_FINITE_NUMBER");
    return value;
  }
  if (Array.isArray(value)) return value.map(canonicalize);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => compareCanonical(left, right))
        .map(([key, entry]) => [key, canonicalize(entry)]),
    );
  }
  throw new Error(`UNSUPPORTED_CANONICAL_VALUE:${typeof value}`);
}

export function stableStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

export function assertContentHash(
  value: string,
  label: string,
): asserts value is ContentHash {
  if (!HASH_PATTERN.test(value))
    throw new Error(`INVALID_CONTENT_HASH:${label}`);
}

export function assertIdentifier(value: unknown, label: string): void {
  if (typeof value !== "string" || !IDENTIFIER_PATTERN.test(value)) {
    throw new Error(`INVALID_IDENTIFIER:${label}`);
  }
}

export function uniqueSorted<T extends string>(
  values: T[],
  label: string,
): T[] {
  if (values.some((value) => value.trim().length === 0)) {
    throw new Error(`EMPTY_VALUE:${label}`);
  }
  const sorted = [...values].sort(compareCanonical);
  if (new Set(sorted).size !== sorted.length) {
    throw new Error(`DUPLICATE_VALUE:${label}`);
  }
  return sorted;
}

export function cloneJson<T>(value: T): T {
  return JSON.parse(stableStringify(value)) as T;
}
